import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Drawer,
  Toolbar,
  IconButton,
} from '@mui/material';
import {
  EmojiEvents as ContestIcon,
  Menu as MenuIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PlayerSidebar from '@components/layout/PlayerSidebar';
import { CORE_SPORTS } from '@utils/sports';
import { getPortalPaths, type PortalMode } from '@utils/portalPaths';
import { getSportIcon, getSportTheme } from '@utils/sportPresentation';
import api from '@services/api';

const DRAWER_WIDTH = 280;

interface SportContestMetrics {
  sport: string;
  activeContestCount: number;
  hasData: boolean;
}

interface ContestOverviewPageProps {
  portalMode?: PortalMode;
}

const ContestOverviewPage: React.FC<ContestOverviewPageProps> = ({ portalMode = 'player' }) => {
  const navigate = useNavigate();
  const paths = getPortalPaths(portalMode);
  const hideSidebar = portalMode === 'coach';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sportMetrics, setSportMetrics] = useState<SportContestMetrics[]>([]);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    loadMetrics();
  }, []);

  const loadMetrics = async () => {
    setLoading(true);
    setError(null);
    try {
      const metrics: SportContestMetrics[] = [];
      for (const sport of CORE_SPORTS) {
        try {
          const response = await api.get(`/contest-tasks?sport=${encodeURIComponent(sport)}`);
          const tasks = response.data?.data || [];
          const count = Array.isArray(tasks) ? tasks.length : 0;
          metrics.push({ sport, activeContestCount: count, hasData: count > 0 });
        } catch {
          metrics.push({ sport, activeContestCount: 0, hasData: false });
        }
      }
      setSportMetrics([...metrics.filter((m) => m.hasData), ...metrics.filter((m) => !m.hasData)]);
    } catch (err: any) {
      setError(err?.message || 'Failed to load contest data');
    } finally {
      setLoading(false);
    }
  };

  const handleSportClick = (sport: string) => {
    navigate(paths.contestSport(sport, 'all'));
  };

  const mainContent = (
    <Container maxWidth="lg" sx={{ mb: 4, pt: hideSidebar ? 2 : 0 }}>
      <Box
        sx={{
          mb: 3,
          p: 3,
          borderRadius: 3,
          color: '#fff',
          background:
            'radial-gradient(circle at 12% 20%, rgba(255,255,255,0.22), transparent 24%), linear-gradient(135deg, #7c2d12 0%, #ea580c 45%, #c2410c 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: 180,
            height: 180,
            right: -55,
            top: -65,
            borderRadius: '50%',
            border: '28px solid rgba(255,255,255,0.14)',
          }}
        />
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 950, position: 'relative' }}>
          <ContestIcon sx={{ fontSize: 34, color: '#fff' }} />
          Contests
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', maxWidth: 760, position: 'relative' }}>
          Browse open contests by sport, watch training videos, submit performance clips, and track leaderboard results by category.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {sportMetrics.map((metric) => {
            const theme = getSportTheme(metric.sport);
            return (
              <Paper
                key={metric.sport}
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  borderColor: theme.border,
                  background: theme.gradient,
                  boxShadow: metric.hasData ? `0 12px 28px ${theme.main}18` : 'none',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '260px 1fr auto' },
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Box
                      sx={{
                        width: 54,
                        height: 54,
                        borderRadius: 2.5,
                        bgcolor: theme.light,
                        color: theme.main,
                        border: `1px solid ${theme.border}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {getSportIcon(metric.sport, 34)}
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 950, color: theme.main, lineHeight: 1.1 }}>
                        {metric.sport}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {metric.hasData
                          ? `${metric.activeContestCount} active contest${metric.activeContestCount === 1 ? '' : 's'}`
                          : 'No contests published yet'}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    <Chip
                      label={metric.hasData ? 'Open for submissions' : 'Coming soon'}
                      size="small"
                      sx={{
                        bgcolor: metric.hasData ? '#fff7ed' : '#f1f5f9',
                        color: metric.hasData ? '#c2410c' : 'text.secondary',
                        border: `1px solid ${metric.hasData ? '#fdba74' : '#e2e8f0'}`,
                        fontWeight: 800,
                      }}
                    />
                  </Box>

                  <Button
                    variant="contained"
                    size="medium"
                    onClick={() => handleSportClick(metric.sport)}
                    disabled={false}
                    sx={{
                      justifySelf: { xs: 'stretch', md: 'end' },
                      bgcolor: theme.main,
                      color: '#fff',
                      borderRadius: 999,
                      px: 2.5,
                      fontWeight: 950,
                      textTransform: 'none',
                      '&:hover': { bgcolor: theme.main, filter: 'brightness(0.92)' },
                    }}
                  >
                    View Contests
                  </Button>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      {sportMetrics.some((m) => m.hasData) && (
        <Paper sx={{ mt: 4, p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 800 }}>
            <AssessmentIcon sx={{ color: '#c2410c' }} />
            Contest Hub
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {sportMetrics.filter((m) => m.hasData).length} sport
            {sportMetrics.filter((m) => m.hasData).length === 1 ? '' : 's'} with active contests. Select a sport to browse
            by age category, submit videos, and view leaderboards.
          </Typography>
        </Paper>
      )}
    </Container>
  );

  if (hideSidebar) {
    return mainContent;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}
      >
        <PlayerSidebar />
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{ display: { xs: 'none', md: 'block' }, width: DRAWER_WIDTH, '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}
        open
      >
        <PlayerSidebar />
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, width: { md: `calc(100% - ${DRAWER_WIDTH}px)` } }}>
        <Toolbar sx={{ display: { md: 'none' } }}>
          <IconButton onClick={() => setMobileOpen(true)}>
            <MenuIcon />
          </IconButton>
        </Toolbar>
        {mainContent}
      </Box>
    </Box>
  );
};

export default ContestOverviewPage;
